import React, { useState, useCallback, useEffect } from "react";
import { useFindMany } from "@gadgetinc/react";
import { PlusIcon, TrashIcon } from "@heroicons/react/16/solid";
import { api } from "../api";
import { Badge } from "./tailwind/badge";
import { Button } from "./tailwind/button";
import { Input } from "./tailwind/input";
import { Field, Fieldset } from "./tailwind/fieldset";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./tailwind/table";
import LoadingSpinner from "./LoadingSpinner";

const ProductPicker = ({
  onSelectionChange,
  selectedVariants = [],
  onCreateDraftOrder,
  className = "",
}) => {
  const [selectedRows, setSelectedRows] = useState(selectedVariants);

  // Sync selectedVariants prop with internal state
  useEffect(() => {
    setSelectedRows(selectedVariants);
  }, [selectedVariants]);

  const [search, setSearch] = useState("");
  const [criteria, setCriteria] = useState({
    filter: {
      product: { isSet: true }, // Only show variants that have a product
      inventoryQuantity: { greaterThan: 0 }, // Only show variants with inventory
    },
  });

  // Search functionality - search products by name
  const [{ data: searchData }] = useFindMany(api.shopifyProduct, {
    search,
    select: { id: true },
  });

  // Update criteria based on search results
  useEffect(() => {
    if (search) {
      if (searchData) {
        const productIds = searchData.map(({ id }) => id);
        setCriteria({
          filter: {
            inventoryQuantity: { greaterThan: 0 },
            productId: {
              in: productIds.length > 0 ? productIds : [0],
            },
          },
        });
      }
    } else {
      setCriteria({
        filter: {
          product: { isSet: true },
          inventoryQuantity: { greaterThan: 0 },
        },
      });
    }
  }, [searchData, search]);

  // Fetch product variants with inventory
  const [{ data: variants, fetching, error }] = useFindMany(
    api.shopifyProductVariant,
    {
      ...criteria,
      select: {
        id: true,
        title: true,
        price: true,
        sku: true,
        inventoryQuantity: true,
        product: {
          id: true,
          title: true,
          vendor: true,
        },
        shop: {
          id: true,
          currency: true,
          moneyFormat: true,
        },
      },
      sort: {
        createdAt: "Descending",
      },
      first: 10,
      live: true,
    },
  );

  // Sort variants to show selected ones first
  const sortedVariants = [...(variants || [])].sort((a, b) => {
    const aSelected = selectedRows.some((variant) => variant.id === a.id);
    const bSelected = selectedRows.some((variant) => variant.id === b.id);

    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return 0;
  });

  // Helper function to format price with currency
  const formatPrice = (price, currency = "USD") => {
    if (!price) return "0.00";

    // Common currency symbols
    const currencySymbols = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      CAD: "C$",
      AUD: "A$",
      JPY: "¥",
      CHF: "CHF",
      SEK: "kr",
      NOK: "kr",
      DKK: "kr",
    };

    const symbol = currencySymbols[currency] || currency;
    return `${symbol}${parseFloat(price).toFixed(2)}`;
  };

  const handleRowClick = useCallback(
    (row) => {
      const variantId = row.id;
      const isSelected = selectedRows.some(
        (variant) => variant.id === variantId,
      );

      let newSelection;
      if (isSelected) {
        // Remove from selection
        newSelection = selectedRows.filter(
          (variant) => variant.id !== variantId,
        );
      } else {
        // Add to selection with default quantity of 1
        newSelection = [...selectedRows, { ...row, quantity: 1 }];
      }

      setSelectedRows(newSelection);
      onSelectionChange?.(newSelection);
    },
    [selectedRows, onSelectionChange],
  );

  const handleQuantityChange = useCallback(
    (variantId, newQuantity) => {
      const newSelection = selectedRows.map((variant) =>
        variant.id === variantId
          ? { ...variant, quantity: Math.max(1, newQuantity) }
          : variant,
      );

      setSelectedRows(newSelection);
      onSelectionChange?.(newSelection);
    },
    [selectedRows, onSelectionChange],
  );

  const removeVariant = useCallback(
    (variantId) => {
      const newSelection = selectedRows.filter(
        (variant) => variant.id !== variantId,
      );
      setSelectedRows(newSelection);
      onSelectionChange?.(newSelection);
    },
    [selectedRows, onSelectionChange],
  );

  const getRowClassName = useCallback(
    (row) => {
      const isSelected = selectedRows.some((variant) => variant.id === row.id);
      return isSelected ? "bg-blue-50" : "";
    },
    [selectedRows],
  );

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header Section - Fixed */}
      <div className="flex-shrink-0 space-y-4 mt-2">
        {/* Summary */}
        {selectedRows.length > 0 && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <strong>{selectedRows.length}</strong> product
                {selectedRows.length > 1 ? "s" : ""} selected
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-sm font-medium text-gray-900">
                  Total:{" "}
                  {selectedRows.length > 0 && selectedRows[0].shop?.currency
                    ? formatPrice(
                        selectedRows.reduce(
                          (sum, variant) =>
                            sum +
                            parseFloat(variant.price || 0) * variant.quantity,
                          0,
                        ),
                        selectedRows[0].shop.currency,
                      )
                    : "0.00"}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  color="secondaryButton"
                  onClick={() => {
                    setSelectedRows([]);
                    onSelectionChange([]);
                  }}
                >
                  Clear Selection
                </Button>
                <Button
                  onClick={() => {
                    if (selectedRows.length === 0) {
                      alert("Please select at least one product variant.");
                      return;
                    }
                    if (onCreateDraftOrder) {
                      const selectedData = selectedRows.map((variant) => ({
                        variantId: variant.id,
                        variantTitle: variant.title,
                        productTitle: variant.product?.title,
                        quantity: variant.quantity || 1,
                        price: variant.price,
                        currency: variant.shop?.currency,
                      }));
                      onCreateDraftOrder(selectedData);
                    }
                  }}
                  disabled={selectedRows.length === 0}
                  color="primaryButton"
                  size="sm"
                >
                  Select ({selectedRows.length} items)
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div>
          <div className="w-64">
            <Fieldset>
              <Field>
                <Input
                  autoComplete="off"
                  name="search-products"
                  placeholder="Search by product name..."
                  value={search ?? ""}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Field>
            </Fieldset>
          </div>
        </div>
      </div>

      {/* Scrollable Table Section */}
      <div className="flex-1 overflow-hidden">
        {fetching ? (
          <div className="flex items-center justify-center h-full">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-red-500">
              <div className="text-lg font-semibold mb-2">
                Unable to load products
              </div>
              <div className="text-sm">
                Please try refreshing the page or contact support if the issue
                persists.
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full overflow-y-auto">
            <Table className="cursor-pointer">
              <TableHead>
                <TableRow>
                  <TableHeader>Product</TableHeader>
                  <TableHeader>Variant</TableHeader>
                  <TableHeader>Price</TableHeader>
                  <TableHeader>Inventory</TableHeader>
                  <TableHeader className="text-right">Quantity</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedVariants && sortedVariants.length > 0 ? (
                  sortedVariants.map((variant) => {
                    const isSelected = selectedRows.some(
                      (selected) => selected.id === variant.id,
                    );
                    const selectedVariant = selectedRows.find(
                      (selected) => selected.id === variant.id,
                    );
                    return (
                      <TableRow
                        key={variant.id}
                        onClick={() => handleRowClick(variant)}
                        className={getRowClassName(variant)}
                      >
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {variant.product?.title || "Unknown Product"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {variant.product?.vendor || ""}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{variant.title}</div>
                            {variant.sku && (
                              <div className="text-sm text-gray-500">
                                SKU: {variant.sku}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-green-600">
                            {formatPrice(variant.price, variant.shop?.currency)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              variant.inventoryQuantity > 0
                                ? "default"
                                : "destructive"
                            }
                            className="text-xs"
                          >
                            {variant.inventoryQuantity} in stock
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {isSelected ? (
                            <div className="flex items-center justify-end space-x-2">
                              <input
                                type="number"
                                min="1"
                                value={selectedVariant?.quantity || 1}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleQuantityChange(
                                    variant.id,
                                    parseInt(e.target.value) || 1,
                                  );
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-16 px-2 py-1 border rounded text-center"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeVariant(variant.id);
                                }}
                                className="text-red-600 hover:text-red-700 p-1 h-6 w-6"
                              >
                                <TrashIcon className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(variant);
                                }}
                                className="text-green-600 hover:text-green-700 p-1 h-6 w-6"
                              >
                                <PlusIcon className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-gray-500 py-8"
                    >
                      {search ? (
                        <>
                          <div className="text-lg font-semibold mb-2">
                            No products found
                          </div>
                          <div className="text-sm">
                            Try adjusting your search terms or clear the search
                            to see all products.
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-lg font-semibold mb-2">
                            No products available
                          </div>
                          <div className="text-sm">
                            All product variants are currently out of stock.
                          </div>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPicker;
